/**
 * Client-side request helper: refresh once, retry once, de-duplicated.
 *
 * Two rules here are load-bearing, and both come from outages rather than
 * theory (§3.3, §3.6).
 */

import { APP_SLUG, HUB_URL } from "./config";
import { tryRefresh } from "./refresh";

export { tryRefresh };

/** "The server refused" and "the server said nothing" are different facts.
 *  A 401/403 means the session really is over. A 5xx, a timeout or a dropped
 *  connection says NOTHING about the session — treating it as a rejection is
 *  what logs everybody out during a deploy. */
export const NO_RESPONSE = 0;

export interface ApiResult<T> {
  status: number;
  data: T | null;
}

export async function request<T>(path: string, init: RequestInit = {}, isRetry = false): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(path, { ...init, credentials: "include" });
  } catch {
    // No answer at all. Say nothing about the session.
    return { status: NO_RESPONSE, data: null };
  }

  if (res.status === 401 && !isRetry) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(path, init, true);
    redirectToLogin();
    return { status: 401, data: null };
  }

  let data: T | null;
  try {
    data = (await res.json()) as T;
  } catch {
    data = null;
  }

  // The Hub renders a modal naming the app — hence the `app` param. Without it
  // the user gets a generic "no access" with nothing to ask their admin for.
  if (res.status === 403 && (data as { code?: string } | null)?.code === "NO_APP_PERMISSION") {
    window.location.href = `${HUB_URL}?error=no_permission&app=${APP_SLUG}`;
  }

  return { status: res.status, data };
}

export function redirectToLogin(): void {
  window.location.href = `${HUB_URL}/auth/slack?redirect=${encodeURIComponent(window.location.origin)}`;
}
